import React, { useState, useEffect, memo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  Alert,
  Chip,
  CircularProgress,
  InputAdornment,
  Typography
} from '@mui/material';
import { 
  Image as ImageIcon, 
  CheckCircle, 
  Warning, 
  Error as ErrorIcon 
} from '@mui/icons-material';
import uploadService from '../services/uploadService';
import productService from '../services/productService';

const DEFAULT_PRICE_PERCENTAGES = {
  lista1: 25,
  lista2: 35,
  lista3: 45,
  consumidorFinal: 60
};

const ProductFormDialog = memo(({ 
  open, 
  editingProduct, 
  categories, 
  brands, 
  onCreateCategory,
  onCreateBrand,
  onClose, 
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    varietal: '',
    vintage: '',
    region: '',
    volume: '',
    alcoholContent: '',
    tastingNotes: '',
    pairing: '',
    price: '',
    cost: '',
    precioLista1: '',
    precioLista2: '',
    precioLista3: '',
    stock: '',
    minStock: 5,
    categoryId: '',
    brandId: '',
    showInCatalog: true,
    image: ''
  });
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newBrandName, setNewBrandName] = useState('');
  const [creatingOption, setCreatingOption] = useState('');

  // Estados para porcentajes de ganancia
  const [porcentajes, setPorcentajes] = useState(DEFAULT_PRICE_PERCENTAGES);
  // Estados para validación de código
  const [codeValidation, setCodeValidation] = useState({
    checking: false,
    disponible: null,
    message: '',
    tipo: null,
    sugerencias: []
  });
  const codeCheckTimeout = useRef(null);

  // Cargar porcentajes guardados desde localStorage al iniciar
  useEffect(() => {
    const savedPorcentajes = localStorage.getItem('preciosListaPorcentajes');
    if (savedPorcentajes) {
      try {
        const parsed = JSON.parse(savedPorcentajes);
        const hasConfiguredMargin = Object.values(parsed).some((value) => Number(value) > 0);
        if (hasConfiguredMargin) {
          setPorcentajes(parsed);
        } else {
          setPorcentajes(DEFAULT_PRICE_PERCENTAGES);
          localStorage.setItem('preciosListaPorcentajes', JSON.stringify(DEFAULT_PRICE_PERCENTAGES));
        }
      } catch (e) {
        console.error('Error al cargar porcentajes:', e);
      }
    } else {
      localStorage.setItem('preciosListaPorcentajes', JSON.stringify(DEFAULT_PRICE_PERCENTAGES));
    }
  }, []);

  // Calcular precios basados en costo y porcentajes de ganancia
  useEffect(() => {
    const costo = parseFloat(formData.cost);
    // Los DECIMAL de PostgreSQL llegan como texto (por ejemplo "0.00").
    // Un costo cero significa "sin costo informado" y no debe pisar los
    // precios importados desde la lista del proveedor.
    if (Number.isFinite(costo) && costo > 0) {
      
      setFormData(prev => ({
        ...prev,
        precioLista1: porcentajes.lista1 !== 0 
          ? (costo * (1 + porcentajes.lista1 / 100)).toFixed(2)
          : '',
        precioLista2: porcentajes.lista2 !== 0 
          ? (costo * (1 + porcentajes.lista2 / 100)).toFixed(2)
          : '',
        precioLista3: porcentajes.lista3 !== 0 
          ? (costo * (1 + porcentajes.lista3 / 100)).toFixed(2)
          : '',
        price: porcentajes.consumidorFinal !== 0 
          ? (costo * (1 + porcentajes.consumidorFinal / 100)).toFixed(2)
          : prev.price
      }));
    }
  }, [formData.cost, porcentajes]);

  useEffect(() => {
    if (editingProduct) {
      setFormData({
        code: editingProduct.code || '',
        name: editingProduct.name,
        description: editingProduct.description || '',
        varietal: editingProduct.varietal || '',
        vintage: editingProduct.vintage || '',
        region: editingProduct.region || '',
        volume: editingProduct.volume || '',
        alcoholContent: editingProduct.alcoholContent || '',
        tastingNotes: editingProduct.tastingNotes || '',
        pairing: editingProduct.pairing || '',
        price: editingProduct.price,
        cost: editingProduct.cost || '',
        precioLista1: editingProduct.precioLista1 || '',
        precioLista2: editingProduct.precioLista2 || '',
        precioLista3: editingProduct.precioLista3 || '',
        stock: editingProduct.stock,
        minStock: editingProduct.minStock,
        categoryId: editingProduct.categoryId || '',
        brandId: editingProduct.brandId || '',
        showInCatalog: editingProduct.showInCatalog,
        image: editingProduct.image || ''
      });
    } else {
      setFormData({
        code: '',
        name: '',
        description: '',
        varietal: '',
        vintage: '',
        region: '',
        volume: '',
        alcoholContent: '',
        tastingNotes: '',
        pairing: '',
        price: '',
        cost: '',
        precioLista1: '',
        precioLista2: '',
        precioLista3: '',
        stock: '',
        minStock: 5,
        categoryId: '',
        brandId: '',
        showInCatalog: true,
        image: ''
      });
    }
    // Resetear validación de código
    setCodeValidation({
      checking: false,
      disponible: null,
      message: '',
      tipo: null,
      sugerencias: []
    });
  }, [editingProduct, open]);

  useEffect(() => {
    if (open) {
      setNewCategoryName('');
      setNewBrandName('');
      setCreatingOption('');
    }
  }, [open]);

  // Validación de código con debounce
  useEffect(() => {
    // Limpiar timeout anterior
    if (codeCheckTimeout.current) {
      clearTimeout(codeCheckTimeout.current);
    }

    // Si no hay código, resetear validación
    if (!formData.code || formData.code.trim() === '') {
      setCodeValidation({
        checking: false,
        disponible: null,
        message: '',
        tipo: null,
        sugerencias: []
      });
      return;
    }

    // Si estamos editando y el código es el mismo que el original, no mostrar validación
    if (editingProduct && formData.code === editingProduct.code) {
      setCodeValidation({
        checking: false,
        disponible: null,
        message: '',
        tipo: null,
        sugerencias: []
      });
      return;
    }

    // Mostrar indicador de carga
    setCodeValidation(prev => ({ ...prev, checking: true }));

    // Verificar código después de 500ms
    codeCheckTimeout.current = setTimeout(async () => {
      const result = await productService.checkCode(
        formData.code, 
        editingProduct?.id
      );

      if (result.success) {
        setCodeValidation({
          checking: false,
          disponible: result.data.disponible,
          message: result.data.message,
          tipo: result.data.tipo,
          sugerencias: result.data.sugerencias || []
        });
      } else {
        setCodeValidation({
          checking: false,
          disponible: null,
          message: '',
          tipo: null,
          sugerencias: []
        });
      }
    }, 500);

    return () => {
      if (codeCheckTimeout.current) {
        clearTimeout(codeCheckTimeout.current);
      }
    };
  }, [formData.code, editingProduct]);

  // Handler ultra-optimizado que no se recrea
  const handleChange = React.useCallback((field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleRelationChange = React.useCallback((field, type) => (event) => {
    if (event.target.value === '__new__') {
      setCreatingOption(type);
      return;
    }
    setCreatingOption('');
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  }, []);

  const createRelation = React.useCallback(async (type) => {
    const isCategory = type === 'category';
    const name = (isCategory ? newCategoryName : newBrandName).trim();
    if (!name) return;
    const created = await (isCategory ? onCreateCategory(name) : onCreateBrand(name));
    if (created) {
      setFormData((prev) => ({ ...prev, [isCategory ? 'categoryId' : 'brandId']: created.id }));
      if (isCategory) setNewCategoryName('');
      else setNewBrandName('');
      setCreatingOption('');
    }
  }, [newCategoryName, newBrandName, onCreateCategory, onCreateBrand]);

  const handleImageBlur = React.useCallback(() => {
    setFormData(prev => ({
      ...prev,
      image: uploadService.normalizeExternalImageUrl(prev.image)
    }));
  }, []);

  const handleFormSubmit = React.useCallback(() => {
    onSubmit({
      ...formData,
      image: uploadService.normalizeExternalImageUrl(formData.image)
    }, editingProduct);
  }, [onSubmit, formData, editingProduct]);

  // Evita cerrar accidentalmente al hacer click fuera del modal o presionar Esc.
  const handleDialogClose = React.useCallback((event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  }, [onClose]);

  // Aplicar sugerencia de código
  const applySuggestion = React.useCallback((suggestion) => {
    setFormData(prev => ({ ...prev, code: suggestion }));
  }, []);

  // Manejar cambio de porcentajes y guardar en localStorage
  const handlePorcentajeChange = React.useCallback((lista) => (e) => {
    const value = parseFloat(e.target.value) || 0;
    const newPorcentajes = {
      ...porcentajes,
      [lista]: value
    };
    setPorcentajes(newPorcentajes);
    // Guardar en localStorage
    localStorage.setItem('preciosListaPorcentajes', JSON.stringify(newPorcentajes));
  }, [porcentajes]);

  const restoreSuggestedPercentages = React.useCallback(() => {
    setPorcentajes(DEFAULT_PRICE_PERCENTAGES);
    localStorage.setItem('preciosListaPorcentajes', JSON.stringify(DEFAULT_PRICE_PERCENTAGES));
  }, []);

  // Obtener color del campo según validación
  const getCodeFieldColor = () => {
    if (codeValidation.checking) return undefined;
    if (codeValidation.disponible === true) return 'success';
    if (codeValidation.disponible === false) {
      return codeValidation.tipo === 'eliminado' ? 'warning' : 'error';
    }
    return undefined;
  };

  // Obtener icono del campo según validación
  const getCodeIcon = () => {
    if (codeValidation.checking) {
      return <CircularProgress size={20} />;
    }
    if (codeValidation.disponible === true) {
      return <CheckCircle color="success" />;
    }
    if (codeValidation.disponible === false) {
      if (codeValidation.tipo === 'eliminado') {
        return <Warning color="warning" />;
      }
      return <ErrorIcon color="error" />;
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {editingProduct ? 'Editar etiqueta o bebida' : 'Nueva etiqueta o bebida'}
      </DialogTitle>
      <DialogContent>
          <Grid container spacing={2} className="mt-xs">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código"
                value={formData.code}
                onChange={handleChange('code')}
                autoComplete="off"
                color={getCodeFieldColor()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {getCodeIcon()}
                    </InputAdornment>
                  )
                }}
              />
              
              {/* Mensaje de validación inline */}
              {codeValidation.disponible === true && (
                <Alert severity="success" className="mt-sm" icon={<CheckCircle />}>
                  ✓ Código disponible
                </Alert>
              )}
              
              {codeValidation.disponible === false && codeValidation.tipo === 'activo' && (
                <Alert severity="error" className="mt-sm" icon={<ErrorIcon />}>
                  ✗ {codeValidation.message}
                </Alert>
              )}
              
              {codeValidation.disponible === false && codeValidation.tipo === 'eliminado' && (
                <Alert severity="warning" className="mt-sm" icon={<Warning />}>
                  <Box>
                    <Box className="mb-sm">
                      ⚠️ {codeValidation.message}
                    </Box>
                    {codeValidation.sugerencias.length > 0 && (
                      <Box className="d-flex flex-wrap gap-xs">
                        <Box component="span" className="text-secondary mr-xs" style={{ fontSize: '0.875rem' }}>
                          Sugerencias:
                        </Box>
                        {codeValidation.sugerencias.map((sugerencia, index) => (
                          <Chip
                            key={index}
                            label={sugerencia}
                            size="small"
                            color="warning"
                            variant="outlined"
                            onClick={() => applySuggestion(sugerencia)}
                            style={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Alert>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre"
                value={formData.name}
                onChange={handleChange('name')}
                required
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={formData.description}
                onChange={handleChange('description')}
                multiline
                rows={3}
                autoComplete="off"
              />
            </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Categoría"
              value={formData.categoryId}
              onChange={handleRelationChange('categoryId', 'category')}
            >
              <MenuItem value="">Sin categoría</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
              <MenuItem value="__new__">+ Crear nueva categoría</MenuItem>
            </TextField>
            {creatingOption === 'category' && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField size="small" fullWidth autoFocus label="Nombre de la nueva categoría" value={newCategoryName} onChange={(event) => setNewCategoryName(event.target.value)} />
                <Button variant="outlined" onClick={() => createRelation('category')} disabled={!newCategoryName.trim()}>Crear</Button>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Marca"
              value={formData.brandId}
              onChange={handleRelationChange('brandId', 'brand')}
            >
              <MenuItem value="">Sin marca</MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
              <MenuItem value="__new__">+ Crear nueva marca</MenuItem>
            </TextField>
            {creatingOption === 'brand' && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField size="small" fullWidth autoFocus label="Nombre de la nueva marca" value={newBrandName} onChange={(event) => setNewBrandName(event.target.value)} />
                <Button variant="outlined" onClick={() => createRelation('brand')} disabled={!newBrandName.trim()}>Crear</Button>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} className="form-section">
            <Box className="form-section-header wine-data-section">
              <Typography variant="subtitle2" className="section-title">
                Datos de la etiqueta
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth label="Varietal o estilo" value={formData.varietal} onChange={handleChange('varietal')} placeholder="Ej: Malbec, Extra Brut, London Dry" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth label="Añada" value={formData.vintage} onChange={handleChange('vintage')} placeholder="Ej: 2023 o Sin añada" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth label="Región de origen" value={formData.region} onChange={handleChange('region')} placeholder="Ej: Valle de Tulum, San Juan" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth label="Presentación" value={formData.volume} onChange={handleChange('volume')} placeholder="Ej: 750 ml" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Graduación alcohólica"
              value={formData.alcoholContent}
              onChange={handleChange('alcoholContent')}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">% vol.</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Notas de cata" value={formData.tastingNotes} onChange={handleChange('tastingNotes')} placeholder="Aromas, cuerpo, textura y final" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Maridaje recomendado" value={formData.pairing} onChange={handleChange('pairing')} placeholder="Ej: carnes rojas, pastas y quesos maduros" />
          </Grid>
          {/* Costo Base */}
          <Grid item xs={12} className="form-section">
            <Box className="form-section-header cost-section">
              <Typography variant="subtitle2" className="section-title">
                💵 Costo de Adquisición (Base para cálculo)
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Costo"
              type="number"
              value={formData.cost}
              onChange={handleChange('cost')}
              required
              autoComplete="off"
              inputProps={{ step: "0.01" }}
              helperText="Precio de compra al proveedor - Base para calcular ganancias"
            />
          </Grid>

          {/* Listas de Precios con Porcentajes de Ganancia */}
          <Grid item xs={12} className="form-section">
            <Box className="form-section-header price-lists-section">
              <Box>
                <Typography variant="subtitle2" className="section-title">
                  Calculador de precios
                </Typography>
                <Typography variant="caption">
                  Ingresá el costo una vez y ajustá los márgenes si lo necesitás.
                </Typography>
              </Box>
              <Button size="small" variant="outlined" onClick={restoreSuggestedPercentages}>
                Usar márgenes sugeridos
              </Button>
            </Box>
          </Grid>
          
          {/* Lista 1 */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Margen mayorista"
              type="number"
              value={porcentajes.lista1}
              onChange={handlePorcentajeChange('lista1')}
              autoComplete="off"
              inputProps={{ step: "0.1", min: "0" }}
              helperText="Sugerido: 25%"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              className="calculated-field"
              label="Precio mayorista"
              type="number"
              value={formData.precioLista1}
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              helperText={porcentajes.lista1 !== 0 
                ? `Costo + ${porcentajes.lista1}% ganancia` 
                : 'Configure el % de ganancia'}
            />
          </Grid>

          {/* Lista 2 */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Margen para comercios"
              type="number"
              value={porcentajes.lista2}
              onChange={handlePorcentajeChange('lista2')}
              autoComplete="off"
              inputProps={{ step: "0.1", min: "0" }}
              helperText="Sugerido: 35%"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              className="calculated-field"
              label="Precio para comercios"
              type="number"
              value={formData.precioLista2}
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              helperText={porcentajes.lista2 !== 0 
                ? `Costo + ${porcentajes.lista2}% ganancia` 
                : 'Configure el % de ganancia'}
            />
          </Grid>

          {/* Lista 3 */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Margen cliente frecuente"
              type="number"
              value={porcentajes.lista3}
              onChange={handlePorcentajeChange('lista3')}
              autoComplete="off"
              inputProps={{ step: "0.1", min: "0" }}
              helperText="Sugerido: 45%"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              className="calculated-field"
              label="Precio cliente frecuente"
              type="number"
              value={formData.precioLista3}
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              helperText={porcentajes.lista3 !== 0 
                ? `Costo + ${porcentajes.lista3}% ganancia` 
                : 'Configure el % de ganancia'}
            />
          </Grid>

          {/* Precio Consumidor Final */}
          <Grid item xs={12} className="form-section">
            <Box className="form-section-header final-price-section">
              <Typography variant="subtitle2" className="section-title">
                Precio de venta al público
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Margen venta al público"
              type="number"
              value={porcentajes.consumidorFinal}
              onChange={handlePorcentajeChange('consumidorFinal')}
              autoComplete="off"
              inputProps={{ step: "0.1", min: "0" }}
              helperText="Precio al público"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              className="calculated-field"
              label="Precio final"
              type="number"
              value={formData.price}
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              helperText={porcentajes.consumidorFinal !== 0 
                ? `Costo + ${porcentajes.consumidorFinal}% ganancia` 
                : 'Configure el % de ganancia'}
            />
          </Grid>

          {/* Stock */}
          <Grid item xs={12} className="form-section">
            <Box className="form-section-header inventory-section">
              <Typography variant="subtitle2" className="section-title">
                📦 Inventario
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Stock"
              type="number"
              value={formData.stock}
              onChange={handleChange('stock')}
              required
              autoComplete="off"
              inputProps={{ step: "1", min: "0" }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Stock Mínimo"
              type="number"
              value={formData.minStock}
              onChange={handleChange('minStock')}
              autoComplete="off"
              inputProps={{ step: "1", min: "0" }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="URL de la imagen"
              value={formData.image}
              onChange={handleChange('image')}
              onBlur={handleImageBlur}
              placeholder="https://drive.google.com/file/d/.../view"
              helperText="Pegá un enlace público de Google Drive o la URL directa de una imagen. En Drive: Compartir → Cualquier persona con el enlace."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ImageIcon />
                  </InputAdornment>
                )
              }}
            />
            {formData.image && (
              <Box className="mt-md text-center">
                <img
                  src={uploadService.getImageUrl(formData.image)}
                  alt="Vista previa"
                  className="img-preview"
                  onError={(event) => {
                    event.currentTarget.src = '/no-image.svg';
                  }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleFormSubmit} variant="contained">
          {editingProduct ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

ProductFormDialog.displayName = 'ProductFormDialog';

export default ProductFormDialog;
